import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  setShipmentContainerAndSealData,
  updateShipmentContainerAndSealNumbers,
} from "../../../features/loadList/loadListSlice";
import { showModal } from "../../../features/modal/modalSlice";
import { useScrollLock } from "../../../hooks/useScrollLock";
import Btn from "../../shared/button/Btn";
import InputText from "../../shared/input/new/InputText";
import SmallSpinner from "../../shared/spinners/SmallSpinner";

const Container = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.6);

  .modal {
    width: min(520px, 100%);
    padding: 28px;
    border-radius: 12px;
    background: ${({theme}) => theme.mainBgColor};
    color: ${({theme}) => theme.mainTextColor};
  }

  .title {
    margin-bottom: 6px;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-xl);
  }

  .shipment-id {
    margin-bottom: 22px;
    color: ${({theme}) => theme.secondaryTextColor};
    font-size: var(--font-size-sm);
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: 22px;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 26px;
  }

  @media (max-width: 520px) {
    .modal {
      padding: 22px;
    }

    .actions {
      flex-direction: column-reverse;
    }

    .actions button {
      width: 100%;
    }
  }
`;

const UpdateShipmentContainerAndSealNumbersModal = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {lockScroll, unlockScroll} = useScrollLock();
  const {modalLocation} = useSelector(state => state.modal);
  const {
    shipmentContainerAndSealData,
    isLoadingUpdateShipmentContainerAndSealNumbers,
  } = useSelector(state => state.loadList);
  const [formData, setFormData] = useState({
    shipmentId: shipmentContainerAndSealData?.shipmentId,
    containerNumber: shipmentContainerAndSealData?.containerNumber ?? "",
    sealNumber: shipmentContainerAndSealData?.sealNumber ?? "",
  });

  useEffect(() => {
    lockScroll();
    return () => unlockScroll();
  }, [lockScroll, unlockScroll]);

  useEffect(() => {
    if (location.pathname !== modalLocation) {
      dispatch(showModal(null));
      dispatch(setShipmentContainerAndSealData(null));
    }
  }, [dispatch, location.pathname, modalLocation]);

  const handleClose = () => {
    if (isLoadingUpdateShipmentContainerAndSealNumbers) return;
    dispatch(showModal(null));
    dispatch(setShipmentContainerAndSealData(null));
  };

  const handleChange = (event) => {
    const {name, value} = event.target;
    setFormData(currentData => ({...currentData, [name]: value}));
  };

  const handleSubmit = () => {
    if (!formData.shipmentId || isLoadingUpdateShipmentContainerAndSealNumbers) return;
    dispatch(updateShipmentContainerAndSealNumbers({
      shipmentId: formData.shipmentId,
      containerNumber: String(formData.containerNumber ?? "").trim(),
      sealNumber: String(formData.sealNumber ?? "").trim(),
    }));
  };

  return (
    <Container onClick={handleClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <div className="title">Konteyner ve Mühür No Güncelle</div>
        <div className="shipment-id">Sevkiyat ID: {formData.shipmentId}</div>

        <div className="fields">
          <InputText
            name="containerNumber"
            labelText="Konteyner No"
            data={formData.containerNumber}
            setData={handleChange}
            inputWidth="100%"
          />
          <InputText
            name="sealNumber"
            labelText="Mühür No"
            data={formData.sealNumber}
            setData={handleChange}
            inputWidth="100%"
          />
        </div>

        <div className="actions">
          <Btn
            width="130px"
            color="#E85446"
            bgColor="transparent"
            handleClick={handleClose}
            isDisabled={isLoadingUpdateShipmentContainerAndSealNumbers}
          >
            İptal
          </Btn>
          <Btn
            width="160px"
            color="#fff"
            bgColor="#E85446"
            borderColor="transparent"
            handleClick={handleSubmit}
            isDisabled={isLoadingUpdateShipmentContainerAndSealNumbers}
          >
            {isLoadingUpdateShipmentContainerAndSealNumbers
              ? <SmallSpinner size="20px" color="#fff" bgColor="#E85446" />
              : "Güncelle"}
          </Btn>
        </div>
      </div>
    </Container>
  );
};

export default UpdateShipmentContainerAndSealNumbersModal;
